/**
 * Quarterly auto-approve reconfirmation UI phases (task 7.4).
 * Calendar math in UTC only — must stay aligned with backend/shared/auto_approve_reconfirm.py
 * and GET /users/channel-account-mappings `autoApproveReconfirm` when present.
 */

export const AUTO_APPROVE_CYCLE_DAYS = 90;
export const AUTO_APPROVE_GRACE_DAYS = 14;
export const AUTO_APPROVE_WEEK_BEFORE_DAYS = 7;

const DEFAULT_CONFIG = {
  cycleDays: AUTO_APPROVE_CYCLE_DAYS,
  graceDays: AUTO_APPROVE_GRACE_DAYS,
  weekBeforeDays: AUTO_APPROVE_WEEK_BEFORE_DAYS,
};

/**
 * @param {object|null|undefined} raw — API `autoApproveReconfirm` object (camelCase)
 */
export function normalizeAutoApproveReconfirmConfig(raw) {
  const pick = (key, def, min, maxOpt) => {
    if (!raw || typeof raw !== 'object') return def;
    const v = Number(raw[key]);
    if (!Number.isFinite(v)) return def;
    const n = Math.floor(v);
    if (n < min) return def;
    if (maxOpt !== undefined && n > maxOpt) return def;
    return n;
  };
  return {
    cycleDays: pick('cycleDays', DEFAULT_CONFIG.cycleDays, 1, 3650),
    graceDays: pick('graceDays', DEFAULT_CONFIG.graceDays, 0, 365),
    weekBeforeDays: pick('weekBeforeDays', DEFAULT_CONFIG.weekBeforeDays, 0, 365),
    graceReminderIntervalDays: pick(
      'graceReminderIntervalDays',
      3,
      1,
      30
    ),
  };
}

/** Today as UTC midnight (calendar date in UTC). */
export function utcCalendarDateFromNow(now = new Date()) {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

/** UTC calendar date of the anchor instant (same rule as DB anchor date). */
export function utcCalendarDateFromIso(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function addUtcDays(utcMidnight, days) {
  const y = utcMidnight.getUTCFullYear();
  const m = utcMidnight.getUTCMonth();
  const day = utcMidnight.getUTCDate();
  return new Date(Date.UTC(y, m, day + days));
}

export function utcDatesEqual(a, b) {
  if (!a || !b) return false;
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

export function utcDateCompare(a, b) {
  const ta = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const tb = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  if (ta < tb) return -1;
  if (ta > tb) return 1;
  return 0;
}

export function formatUtcDateMedium(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function baseUi(cfg) {
  return {
    cycleDays: cfg.cycleDays,
    graceDays: cfg.graceDays,
    weekBeforeDays: cfg.weekBeforeDays,
    graceReminderIntervalDays: cfg.graceReminderIntervalDays,
  };
}

/**
 * @param {object} mapping — API row with auto_approve_enabled, auto_approve_last_reconfirmed_at
 * @param {Date} [now]
 * @param {object|null|undefined} apiReconfirmConfig — from GET mappings `autoApproveReconfirm`
 */
export function computeAutoApproveReconfirmUi(mapping, now = new Date(), apiReconfirmConfig = null) {
  const cfg = normalizeAutoApproveReconfirmConfig(apiReconfirmConfig);
  const none = {
    phase: 'none',
    anchorDate: null,
    dueDate: null,
    graceEndDate: null,
    weekBeforeDate: null,
    ...baseUi(cfg),
  };

  if (!mapping || !mapping.auto_approve_enabled) {
    return none;
  }
  const anchorDate = utcCalendarDateFromIso(mapping.auto_approve_last_reconfirmed_at);
  if (!anchorDate) {
    return none;
  }
  const dueDate = addUtcDays(anchorDate, cfg.cycleDays);
  const graceEndDate = addUtcDays(dueDate, cfg.graceDays);
  const weekBeforeDate = addUtcDays(dueDate, -cfg.weekBeforeDays);
  const today = utcCalendarDateFromNow(now);
  const meta = { anchorDate, dueDate, graceEndDate, weekBeforeDate, ...baseUi(cfg) };

  if (utcDateCompare(today, weekBeforeDate) === 0) {
    return { phase: 'week_before_reminder', ...meta };
  }
  if (utcDateCompare(today, dueDate) >= 0 && utcDateCompare(today, graceEndDate) <= 0) {
    return { phase: 'due_grace', ...meta };
  }
  if (utcDateCompare(today, graceEndDate) > 0) {
    return { phase: 'past_grace', ...meta };
  }
  return {
    phase: 'none',
    ...meta,
  };
}
